import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StagePill, StatusPill, TypePill, ArchiveBadge } from "@/components/pills";
import { fmtDate, fmtHM } from "@/lib/format";
import { STAGE_DESCRIPTIONS, PRIORITY_STYLES, STAGE_COLORS, liveStageSeconds, useNow } from "@/lib/workflow";
import { isAdmin, canApprove as canApproveFn, canTransition, canCreateAsset, allowedTargets } from "@/lib/roles";
import WorkItemDetail from "@/components/WorkItemDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, LayoutGrid, Table2, Clock, User, CalendarClock, ArrowRight, Search, ChevronsUpDown, FileDown, Users, CalendarDays, CalendarRange, MoveRight, Archive } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const emptyForm = { work_item: "", brief: "", work_category: "", sub_category: "", instruction_from: "", work_lead_id: "", priority: "Medium", due_date: "", next_action: "" };

function PriorityBadge({ p }) {
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${PRIORITY_STYLES[p] || ""}`}>{p}</span>;
}

export default function Workflow() {
  const { user, viewMode } = useAuth();
  useNow(1000);
  // MY EMPLOYEE VIEW: an admin acting as an employee is limited to their actual
  // workflow roles (CREATOR / REVIEWER / FINAL_APPROVER) for every permission here.
  const employeeView = isAdmin(user) && viewMode === "employee";
  const effUser = useMemo(() => {
    if (!user || !employeeView) return user;
    const roles = (user.roles || []).filter((r) => r !== "ADMIN");
    return { ...user, roles, role: "member", is_final_approver: roles.includes("FINAL_APPROVER") };
  }, [user, employeeView]);
  const admin = isAdmin(effUser);
  const canApprove = canApproveFn(effUser);
  const canCreate = canCreateAsset(effUser);
  const [period, setPeriod] = useState("month");
  const [wlRange, setWlRange] = useState({ start: "", end: "" });
  const [wlExpanded, setWlExpanded] = useState(null);

  const [config, setConfig] = useState({ stages: [], categories: [], work_categories: [], subcategories: {}, priorities: [], archive_statuses: [], platforms: [] });
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("board");
  const [focusDate, setFocusDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const boardRef = useRef(null);
  const headerRowRef = useRef(null);
  const syncHeader = () => {
    if (boardRef.current && headerRowRef.current) headerRowRef.current.scrollLeft = boardRef.current.scrollLeft;
  };
  const [moveDlg, setMoveDlg] = useState(null); // {id, from, to, comment} — centered stage-move popup

  // table filters
  const [q, setQ] = useState("");
  const [fLead, setFLead] = useState("all");
  const [fStage, setFStage] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fArchive, setFArchive] = useState("all");
  const [sort, setSort] = useState({ key: "work_item_id", dir: 1 });

  const load = useCallback(() => {
    api.get("/assets").then((r) => setAssets(r.data));
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    load();
  }, [load]);

  // keep detail in sync with fresh data
  useEffect(() => {
    if (detail) {
      const fresh = assets.find((a) => a.asset_id === detail.asset_id);
      if (fresh && fresh !== detail) setDetail(fresh);
    }
    // eslint-disable-next-line
  }, [assets]);

  const openNew = () => {
    const firstCat = config.categories?.[0] || "";
    setForm({ ...emptyForm, work_lead_id: user.user_id, work_category: firstCat, sub_category: config.subcategories?.[firstCat]?.[0] || "" });
    setNewOpen(true);
  };

  const subOptions = config.subcategories?.[form.work_category] || [];

  const createItem = async () => {
    if (!form.work_item.trim()) return toast.error("Work Item is required");
    if (!form.work_category) return toast.error("Work Category is required");
    if (!form.sub_category) return toast.error("Sub-category is required");
    const { data } = await api.post("/assets", { ...form, due_date: form.due_date || null });
    setNewOpen(false);
    load();
    toast.success(`Created ${data.work_item_id}`);
  };

  const activeUsers = users.filter((u) => u.status !== "inactive");
  const adminUsers = activeUsers.filter((u) => (u.roles || []).includes("ADMIN"));

  const [searchParams, setSearchParams] = useSearchParams();
  const [deepFlag, setDeepFlag] = useState(null);
  useEffect(() => {
    const stage = searchParams.get("stage");
    const flag = searchParams.get("flag");
    const lead = searchParams.get("lead");
    if (stage || flag || lead) {
      setView("table");
      if (stage) setFStage(stage);
      if (lead) setFLead(lead);
      if (flag) setDeepFlag(flag);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clearDeep = () => { setDeepFlag(null); setFStage("all"); };

  const doTransition = async (id, to, opts = {}) => {
    setAssets((prev) => prev.map((a) => (a.asset_id === id ? { ...a, current_stage: to, stage: to, stage_started_at: new Date().toISOString() } : a)));
    try {
      await api.post(`/assets/${id}/transition`, { to_stage: to, reason: opts.reason || null, instruction: opts.instruction || null, approved: opts.approved || null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move item");
      load();
    }
  };

  const exportXlsx = () => {
    const a = document.createElement("a");
    a.href = `${API}/workflow/export`;
    a.click();
    toast.success("Exporting workflow to Excel…");
  };

  const initiateMove = (id, stage) => {
    const a = assets.find((x) => x.asset_id === id);
    if (!a || a.current_stage === stage) return;
    const frm = a.current_stage;
    if (!canTransition(effUser, frm, stage)) {
      toast.error("You do not have permission to move this Work Item to this stage.");
      return;
    }
    // Centered comment popup — always starts BLANK; the previous stage's comment
    // stays preserved in the workflow history.
    setMoveDlg({ id, from: frm, to: stage, comment: "" });
  };

  const confirmMove = () => {
    const comment = (moveDlg?.comment || "").trim();
    if (!comment) { toast.error("A comment is required to move this Work Item."); return; }
    doTransition(moveDlg.id, moveDlg.to, moveDlg.to === "Complete" ? { approved: true, instruction: comment } : { instruction: comment });
    setMoveDlg(null);
  };

  const onDrop = (stage) => {
    setDragOver(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    initiateMove(id, stage);
  };

  const CLOSED = config.closed_statuses || ["Not Used", "Rejected", "Cancelled", "Internal Use"];
  const active = assets.filter((a) => !CLOSED.includes(a.archive_status));

  // ---- Active Workload + Progress (per Creative Owner) ----
  const periodBounds = () => {
    const now = new Date();
    let start;
    if (period === "week") {
      start = new Date(now);
      const day = (now.getDay() + 6) % 7; // Monday=0
      start.setDate(now.getDate() - day);
      start.setHours(0, 0, 0, 0);
    } else if (period === "year") {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (period === "custom") {
      start = wlRange.start ? new Date(wlRange.start) : new Date(0);
    } else {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    const end = period === "custom" && wlRange.end ? new Date(wlRange.end + "T23:59:59") : new Date();
    return [start, end];
  };
  const inPeriod = (iso) => {
    if (!iso) return false;
    const [s, e] = periodBounds();
    const t = new Date(iso).getTime();
    return t >= s.getTime() && t <= e.getTime();
  };
  const ownerScope = admin ? assets : assets.filter((a) => (a.work_lead_id || a.owner_id) === effUser.user_id);
  const ownerIds = [...new Set(ownerScope.map((a) => a.work_lead_id || a.owner_id).filter(Boolean))];
  const workload = ownerIds.map((oid) => {
    const mine = assets.filter((a) => (a.work_lead_id || a.owner_id) === oid);
    const name = mine[0]?.work_lead_name || mine[0]?.owner_name || "Unassigned";
    const activeItems = mine.filter((a) => a.current_stage !== "Complete" && !CLOSED.includes(a.archive_status));
    const periodItems = mine.filter((a) => inPeriod(a.created_at));
    const completed = periodItems.filter((a) => a.current_stage === "Complete").length;
    const breakdown = ["To Do", "Prepare", "Create", "Review", "Approval"].reduce((m, s) => {
      m[s] = activeItems.filter((a) => a.current_stage === s).length;
      return m;
    }, {});
    return { id: oid, name, active: activeItems.length, completed, total: periodItems.length, breakdown };
  }).sort((a, b) => b.active - a.active);
  const maxLoad = workload.length ? Math.max(...workload.map((w) => w.active)) : 0;
  const PERIOD_LABEL = { week: "This Week", month: "This Month", year: "This Year", custom: "Custom" };

  // table data
  let rows = assets.filter((a) => {
    if (q) {
      const s = `${a.work_item_id} ${a.work_item} ${a.work_category} ${a.next_action || ""} ${a.instruction_from || ""}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    if (fLead !== "all" && a.work_lead_id !== fLead) return false;
    if (fStage !== "all" && a.current_stage !== fStage) return false;
    if (fPriority !== "all" && a.priority !== fPriority) return false;
    if (fArchive !== "all" && a.archive_status !== fArchive) return false;
    if (deepFlag === "overdue" && !(a.due_date && a.current_stage !== "Complete" && new Date(a.due_date) < new Date())) return false;
    if (deepFlag === "source-missing" && a.archive_status !== "Source Link Missing") return false;
    if (deepFlag === "active" && (a.current_stage === "Complete" || CLOSED.includes(a.archive_status))) return false;
    return true;
  });
  const PRIORITY_ORDER = { Low: 0, Medium: 1, High: 2, Urgent: 3 };
  rows = [...rows].sort((a, b) => {
    const k = sort.key;
    let va = a[k], vb = b[k];
    if (k === "priority") { va = PRIORITY_ORDER[a.priority]; vb = PRIORITY_ORDER[b.priority]; }
    if (k === "total_time") { va = a.times?.total_time || 0; vb = b.times?.total_time || 0; }
    if (va == null) va = ""; if (vb == null) vb = "";
    return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
  });
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  // ---- Day / Week views (grouped by last stage-movement date) ----
  const lastMoveAt = (a) => a.stage_started_at || a.updated_at || a.created_at;
  const dateKey = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : null);
  const weekBounds = (dstr) => {
    const d = new Date(dstr + "T00:00:00");
    const day = (d.getDay() + 6) % 7; // Monday = 0
    const mon = new Date(d); mon.setDate(d.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(mon); x.setDate(mon.getDate() + i);
      return x.toISOString().slice(0, 10);
    });
  };
  const shiftDate = (dstr, days) => {
    const d = new Date(dstr + "T00:00:00"); d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const movedOn = (dstr) => assets
    .filter((a) => dateKey(lastMoveAt(a)) === dstr)
    .sort((a, b) => new Date(lastMoveAt(b)) - new Date(lastMoveAt(a)));
  const weekDays = weekBounds(focusDate);

  const ActivityRow = ({ a }) => (
    <tr data-testid={`activity-row-${a.asset_id}`} onClick={() => setDetail(a)} className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer">
      <td className="px-3 py-2.5 font-mono text-primary font-semibold whitespace-nowrap">{a.work_item_id}</td>
      <td className="px-3 py-2.5 font-medium max-w-[220px] truncate">{a.work_item || a.title}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">{a.work_lead_name || "—"}</td>
      <td className="px-3 py-2.5"><StagePill stage={a.current_stage} /></td>
      <td className="px-3 py-2.5"><PriorityBadge p={a.priority} /></td>
      <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{a.due_date ? fmtDate(a.due_date) : "—"}</td>
      <td className="px-3 py-2.5 text-muted-foreground max-w-[220px] truncate">{a.next_action || "—"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground text-xs">{lastMoveAt(a) ? `${fmtDate(lastMoveAt(a))} · ${new Date(lastMoveAt(a)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}</td>
    </tr>
  );
  const ActivityTable = ({ items }) => (
    <div className="rounded-xl border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <thead><tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
          {["ID", "Work Item", "Creative Owner", "Current Stage", "Priority", "Due", "Latest Instruction", "Moved"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
        </tr></thead>
        <tbody>{items.map((a) => <ActivityRow key={a.asset_id} a={a} />)}</tbody>
      </table>
      {items.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">No workflow activity for this period.</p>}
    </div>
  );

  const mainView = () => {
    if (view === "day") return (
      <div className="space-y-4" data-testid="workflow-day-view">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFocusDate(shiftDate(focusDate, -1))}>‹ Prev</Button>
          <Input type="date" value={focusDate} onChange={(e) => setFocusDate(e.target.value)} className="h-8 w-44" data-testid="workflow-day-date" />
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFocusDate(shiftDate(focusDate, 1))}>Next ›</Button>
          <span className="text-sm text-muted-foreground ml-1">{new Date(focusDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        <p className="text-xs text-muted-foreground">Work items whose stage last moved on this day, with where each item currently sits.</p>
        <ActivityTable items={movedOn(focusDate)} />
      </div>
    );
    // week
    return (
      <div className="space-y-4" data-testid="workflow-week-view">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFocusDate(shiftDate(focusDate, -7))}>‹ Prev week</Button>
          <Input type="date" value={focusDate} onChange={(e) => setFocusDate(e.target.value)} className="h-8 w-44" data-testid="workflow-week-date" />
          <Button variant="outline" size="sm" className="h-8" onClick={() => setFocusDate(shiftDate(focusDate, 7))}>Next week ›</Button>
          <span className="text-sm text-muted-foreground ml-1">{fmtDate(weekDays[0])} – {fmtDate(weekDays[6])}</span>
        </div>
        <p className="text-xs text-muted-foreground">Workflow activity across the week, grouped by day.</p>
        {weekDays.map((d) => {
          const items = movedOn(d);
          const isToday = d === new Date().toISOString().slice(0, 10);
          return (
            <div key={d} data-testid={`week-group-${d}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-sm font-semibold ${isToday ? "text-primary" : ""}`}>{new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}</span>
                <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-muted">{items.length}</span>
              </div>
              {items.length > 0 && <ActivityTable items={items} />}
              {items.length === 0 && <p className="text-xs text-muted-foreground pl-1 pb-2">No activity.</p>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fade-up">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <p className="text-muted-foreground text-sm max-w-2xl">
          Drag a card, or tap <strong>Move</strong> on any card (works on phones & tablets), to move it through the pipeline. The system records who moved it, when, and how long it spent in each stage — automatically.
        </p>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-card">
            <button data-testid="view-board-button" onClick={() => setView("board")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <LayoutGrid className="h-4 w-4" /> Board
            </button>
            <button data-testid="view-table-button" onClick={() => setView("table")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Table2 className="h-4 w-4" /> Table
            </button>
            <button data-testid="view-day-button" onClick={() => setView("day")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <CalendarDays className="h-4 w-4" /> Day
            </button>
            <button data-testid="view-week-button" onClick={() => setView("week")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <CalendarRange className="h-4 w-4" /> Week
            </button>
          </div>
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            {canCreate && (
              <DialogTrigger asChild>
                <Button data-testid="new-asset-button" onClick={openNew} className="gap-1.5"><Plus className="h-4 w-4" /> New Asset</Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">New Creative Work Item</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Work Item</Label>
                  <Input data-testid="asset-title-input" value={form.work_item} onChange={(e) => setForm({ ...form, work_item: e.target.value })} className="mt-1" placeholder="e.g. Open Day Hype Video" /></div>
                <div><Label>Brief / Requirements</Label>
                  <Textarea data-testid="asset-brief-input" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} className="mt-1" rows={2} placeholder="What needs to be created and any requirements" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Work Category</Label>
                    <Select value={form.work_category} onValueChange={(v) => setForm({ ...form, work_category: v, sub_category: (config.subcategories?.[v]?.[0] || "") })}>
                      <SelectTrigger data-testid="asset-type-select" className="mt-1"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>{(config.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Sub-category</Label>
                    <Select value={form.sub_category} onValueChange={(v) => setForm({ ...form, sub_category: v })}>
                      <SelectTrigger data-testid="asset-subcategory-select" className="mt-1"><SelectValue placeholder="Sub-category" /></SelectTrigger>
                      <SelectContent>{subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Instruction From</Label>
                    <Select value={form.instruction_from} onValueChange={(v) => setForm({ ...form, instruction_from: v })}>
                      <SelectTrigger data-testid="asset-instruction-select" className="mt-1"><SelectValue placeholder="Who requested" /></SelectTrigger>
                      <SelectContent>{adminUsers.map((u) => <SelectItem key={u.user_id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="asset-priority-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{(config.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Creative Owner</Label>
                    <Select value={form.work_lead_id} onValueChange={(v) => setForm({ ...form, work_lead_id: v })}>
                      <SelectTrigger data-testid="asset-owner-select" className="mt-1"><SelectValue placeholder="Owner" /></SelectTrigger>
                      <SelectContent>{activeUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Due Date</Label>
                    <Input data-testid="asset-deadline-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label>Next Action</Label>
                  <Input data-testid="asset-next-action-input" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="mt-1" placeholder="What happens next" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="save-asset-button" onClick={createItem}>Create Work Item</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {workload.length > 0 && (
        <div data-testid="active-workload" className="mb-5 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold">{admin ? "Active Workload" : "My Active Workload"}</p>
              <span className="text-xs text-muted-foreground hidden sm:inline">Active Creative Work Items currently assigned to each person.</span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="workload-period" className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="year">This Year</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              {period === "custom" && (
                <>
                  <Input type="date" value={wlRange.start} onChange={(e) => setWlRange({ ...wlRange, start: e.target.value })} className="h-8 w-36 text-xs" data-testid="workload-start" />
                  <Input type="date" value={wlRange.end} onChange={(e) => setWlRange({ ...wlRange, end: e.target.value })} className="h-8 w-36 text-xs" data-testid="workload-end" />
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {workload.map((w) => {
              const pct = w.total ? Math.round((w.completed / w.total) * 100) : 0;
              const open = wlExpanded === w.id;
              return (
                <div key={w.id} data-testid={`workload-card-${w.id}`} className="rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40 transition-colors" onClick={() => setWlExpanded(open ? null : w.id)}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">{w.name}</span>
                    <span className="text-xs font-mono font-bold">{w.active} Active</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${w.active === maxLoad && maxLoad > 1 ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${maxLoad ? (w.active / maxLoad) * 100 : 0}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{w.completed} / {w.total} Completed</span>
                    <span className="font-semibold text-foreground">{pct}%</span>
                  </div>
                  {open && (
                    <div className="mt-2 pt-2 border-t border-border grid grid-cols-5 gap-1 text-center" data-testid={`workload-breakdown-${w.id}`}>
                      {["To Do", "Prepare", "Create", "Review", "Approval"].map((s) => (
                        <div key={s}>
                          <p className="text-[9px] uppercase tracking-tight text-muted-foreground">{s === "To Do" ? "Todo" : s === "Approval" ? "Apprv" : s.slice(0, 5)}</p>
                          <p className="text-sm font-bold font-mono">{w.breakdown[s]}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "board" ? (
        <>
          <div className="sticky top-16 z-20 bg-background pt-1 -mx-1 px-1" data-testid="board-stage-header-row">
            <div ref={headerRowRef} className="flex gap-4 overflow-hidden">
              {config.stages.map((stage) => {
                const col = active.filter((a) => a.current_stage === stage);
                const sc = STAGE_COLORS[stage] || STAGE_COLORS["To Do"];
                return (
                  <div key={stage} className="min-w-[300px] w-[300px] shrink-0" data-testid={`column-header-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className={`h-1 w-full rounded-full ${sc.bar} mb-2`} />
                    <div className="mb-2 px-1">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${sc.dot}`} />
                          <StagePill stage={stage} />
                        </span>
                        <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${sc.count}`}>{col.length}</span>
                      </div>
                      <p className="text-[11px] leading-snug text-muted-foreground mt-1.5">{STAGE_DESCRIPTIONS[stage]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div ref={boardRef} onScroll={syncHeader} className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1">
          {config.stages.map((stage) => {
            const col = active.filter((a) => a.current_stage === stage);
            const sc = STAGE_COLORS[stage] || STAGE_COLORS["To Do"];
            return (
              <div key={stage} className="min-w-[300px] w-[300px] shrink-0" data-testid={`column-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                  onDragLeave={() => setDragOver((s) => (s === stage ? null : s))}
                  onDrop={() => onDrop(stage)}
                  className={`space-y-3 rounded-xl border p-3 min-h-[140px] transition-colors ${dragOver === stage ? "bg-primary/5 border-primary/50 border-dashed" : "bg-slate-100/60 dark:bg-slate-900/40 border-border/70"}`}
                >
                  {col.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Drop here</p>}
                  {col.map((a) => (
                    <div
                      key={a.asset_id}
                      data-testid={`asset-card-${a.asset_id}`}
                      draggable
                      onDragStart={() => setDragId(a.asset_id)}
                      onDragEnd={() => { setDragId(null); setDragOver(null); }}
                      onClick={() => setDetail(a)}
                      className={`bg-card border border-border border-l-4 ${sc.accent} rounded-lg p-3.5 shadow-sm hover:shadow-md hover:border-primary/40 transition-all space-y-2.5 cursor-pointer ${dragId === a.asset_id ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-primary font-semibold">{a.work_item_id}</span>
                        <PriorityBadge p={a.priority} />
                      </div>
                      <p className="font-semibold text-sm leading-snug">{a.work_item || a.title}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <TypePill type={a.work_category || a.type} />
                        {a.sub_category && <span className="text-xs text-muted-foreground">{a.sub_category}</span>}
                      </div>
                      {a.current_stage === "Complete" && <ArchiveBadge archiveStatus={a.archive_status} />}
                      {a.work_lead_name && <span className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />{a.work_lead_name}</span>}
                      {a.next_action && <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowRight className="h-3 w-3 shrink-0" /> {a.next_action}</p>}
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border">
                        <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {a.due_date ? fmtDate(a.due_date) : "—"}</span>
                        <span className="flex items-center gap-1 font-mono"><Clock className="h-3 w-3" /> {fmtHM(liveStageSeconds(a))}</span>
                      </div>
                      {(() => {
                        const targets = allowedTargets(effUser, a.current_stage);
                        const canArchive = a.current_stage === "Complete" && (isAdmin(effUser) || canApproveFn(effUser) || a.work_lead_id === user.user_id || a.owner_id === user.user_id);
                        if (targets.length === 0 && !canArchive) return null;
                        const doArchive = async () => {
                          try {
                            const r = await api.post(`/assets/${a.asset_id}/archive`);
                            const msg = r.data?.archive_message;
                            if (r.data?.archive_missing?.length) toast.error(msg || "Archive incomplete");
                            else toast.success(msg || "Archived");
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
                        };
                        return (
                          <div onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button data-testid={`move-btn-${a.asset_id}`} size="sm" variant="outline" className="w-full h-8 gap-1.5 mt-1 text-xs">
                                  <MoveRight className="h-3.5 w-3.5" /> {a.current_stage === "Complete" ? "Archive" : "Move"}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {targets.map((t) => (
                                  <DropdownMenuItem key={t} data-testid={`move-to-${t.replace(/\s+/g, "-").toLowerCase()}-${a.asset_id}`} onClick={() => initiateMove(a.asset_id, t)}>
                                    <ArrowRight className="h-3.5 w-3.5 mr-2" /> {t === "Complete" ? "Approved & Complete" : t}
                                  </DropdownMenuItem>
                                ))}
                                {canArchive && (
                                  <DropdownMenuItem data-testid={`archive-${a.asset_id}`} onClick={doArchive}>
                                    <Archive className="h-3.5 w-3.5 mr-2" /> Send to Archive
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </>
      ) : view === "table" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="workflow-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search work items…" className="pl-9" />
            </div>
            <Select value={fLead} onValueChange={setFLead}><SelectTrigger data-testid="filter-lead" className="w-40"><SelectValue placeholder="Lead" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All leads</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select>
            <Select value={fStage} onValueChange={setFStage}><SelectTrigger data-testid="filter-stage" className="w-36"><SelectValue placeholder="Stage" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All stages</SelectItem>{config.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <Select value={fPriority} onValueChange={setFPriority}><SelectTrigger data-testid="filter-priority" className="w-36"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All priority</SelectItem>{config.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            <Select value={fArchive} onValueChange={setFArchive}><SelectTrigger data-testid="filter-archive" className="w-44"><SelectValue placeholder="Archive status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All archive</SelectItem>{config.archive_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            <Button data-testid="workflow-export-button" onClick={exportXlsx} variant="outline" className="gap-1.5"><FileDown className="h-4 w-4" /> Export</Button>
          </div>

          {deepFlag && (
            <div data-testid="deep-filter-chip" className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
              Filtered: {deepFlag === "source-missing" ? "Source link missing" : deepFlag === "overdue" ? "Overdue" : "Active"}
              <button onClick={clearDeep} className="hover:text-primary/70">✕</button>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card overflow-auto max-h-[calc(100vh-11rem)]">
            <table className="w-full text-sm min-w-[1200px]">
              <thead>
                <tr className="border-b border-border bg-muted text-xs uppercase tracking-wider text-muted-foreground">
                  {[["work_item_id", "ID"], ["work_item", "Work Item"], ["work_category", "Category"], ["sub_category", "Sub-category"], ["instruction_from", "Instruction"], ["work_lead_name", "Lead"], ["priority", "Priority"], ["due_date", "Due"], ["current_stage", "Stage"], ["next_action", "Next Action"]].map(([k, l]) => (
                    <th key={k} onClick={() => toggleSort(k)} className="sticky top-0 z-10 bg-muted text-left font-semibold px-3 py-2.5 cursor-pointer whitespace-nowrap hover:text-foreground">
                      <span className="inline-flex items-center gap-1">{l}<ChevronsUpDown className="h-3 w-3 opacity-50" /></span>
                    </th>
                  ))}
                  <th className="sticky top-0 z-10 bg-muted text-right font-semibold px-3 py-2.5">Prep</th>
                  <th className="sticky top-0 z-10 bg-muted text-right font-semibold px-3 py-2.5">Create</th>
                  <th className="sticky top-0 z-10 bg-muted text-right font-semibold px-3 py-2.5">Review</th>
                  <th className="sticky top-0 z-10 bg-muted text-right font-semibold px-3 py-2.5">Approval</th>
                  <th onClick={() => toggleSort("total_time")} className="sticky top-0 z-10 bg-muted text-right font-semibold px-3 py-2.5 cursor-pointer">Total</th>
                  <th className="sticky top-0 z-10 bg-muted text-left font-semibold px-3 py-2.5 whitespace-nowrap">Completed</th>
                  <th className="sticky top-0 z-10 bg-muted text-left font-semibold px-3 py-2.5">Archive</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.asset_id} data-testid={`table-row-${a.asset_id}`} onClick={() => setDetail(a)} className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer">
                    <td className="px-3 py-2.5 font-mono text-primary font-semibold whitespace-nowrap">{a.work_item_id}</td>
                    <td className="px-3 py-2.5 font-medium max-w-[200px] truncate">{a.work_item || a.title}</td>
                    <td className="px-3 py-2.5"><TypePill type={a.work_category || a.type} /></td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{a.sub_category || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{a.instruction_from || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{a.work_lead_name || "—"}</td>
                    <td className="px-3 py-2.5"><PriorityBadge p={a.priority} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{a.due_date ? fmtDate(a.due_date) : "—"}</td>
                    <td className="px-3 py-2.5"><StagePill stage={a.current_stage} /></td>
                    <td className="px-3 py-2.5 text-muted-foreground max-w-[160px] truncate">{a.next_action || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtHM(a.times?.preparation_time || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtHM(a.times?.creation_time || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtHM(a.times?.review_time || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtHM(a.times?.approval_time || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold">{fmtHM(a.times?.total_time || 0)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap text-xs">{a.completed_at ? `${fmtDate(a.completed_at)} · ${a.completed_by || ""}` : "—"}</td>
                    <td className="px-3 py-2.5">{a.current_stage === "Complete" ? <div className="flex flex-col gap-1"><ArchiveBadge archiveStatus={a.archive_status} /><StatusPill status={a.archive_status} /></div> : <span className="text-muted-foreground text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="text-center text-muted-foreground py-12 text-sm">No work items match your filters.</p>}
          </div>
        </div>
      ) : (
        mainView()
      )}

      {/* Stage-move comment popup — centered; comment always starts blank,
          previous stage's comment remains in the workflow history */}
      <Dialog open={!!moveDlg} onOpenChange={(o) => !o && setMoveDlg(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="move-comment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{moveDlg?.to === "Complete" ? "Approved & Complete" : `Move to ${moveDlg?.to}`}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            {moveDlg?.to === "Complete"
              ? "Enter the approval remark (required). This records you as the approver."
              : `Add a comment / instruction to move this Work Item from ${moveDlg?.from} to ${moveDlg?.to} (required). It is recorded in the workflow history.`}
          </p>
          <Textarea data-testid="move-comment-input" autoFocus value={moveDlg?.comment || ""} onChange={(e) => setMoveDlg({ ...moveDlg, comment: e.target.value })} rows={3} placeholder="Comment for this move…" />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setMoveDlg(null)}>Cancel</Button>
            <Button data-testid="confirm-move-button" onClick={confirmMove}>{moveDlg?.to === "Complete" ? "Approved & Complete" : "Move & record"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WorkItemDetail asset={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} config={config} users={users} user={effUser} canApprove={canApprove} isAdmin={admin} onChanged={load} />
    </div>
  );
}
